/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import styles from './NPI.module.scss';
import { NpiUserListType } from './npiSlice';
import { states } from './us_states';
import { selectPearlUserInfo } from '../sharedcomponents/Login/userSlice';
import { useAppSelector } from '../../app/hooks';

export type NPIFormProps = {
  goBack: () => void;
  fetchNpiList: (params: string) => void;
  userList: NpiUserListType[] | undefined;
  onNpiSelect: (npiNumber: string) => void;
};
export type NPIUserDetailsProps = {
  userDetails: NpiUserListType;
  onNpiSubmit: (npiNumber: string) => void;
};

export default function NPIForm({ goBack, fetchNpiList, userList, onNpiSelect }: NPIFormProps) {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  const [firstName, setFirstName] = useState(pearlUserInfo?.givenName);
  const [lastName, setLastName] = useState(pearlUserInfo?.familyName || '');
  const [selectedState, setSelectedState] = useState('');
  const loadingUserList = useAppSelector((state) => state.npi.loadingUserList);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const params = [
      firstName && `first_name=${firstName}`, // Append firstName if it has a value
      lastName && `last_name=${lastName}`, // Append lastName if it has a value
      selectedState && `state=${selectedState}`,
    ]
      .filter(Boolean)
      .join('&');
    if (params) {
      fetchNpiList(params);
    }
  };

  const selectedStateName = states?.find((state) => state.code === selectedState)?.name;

  return (
    <div className={styles.npiFormContainer} data-testid="npi-form">
      <Typography className={styles.npiDialogTitle} style={{ width: '90%', margin: 0 }}>
        Please provide your first, last name and your state.
      </Typography>
      <Button
        variant="text"
        onClick={goBack}
        className={styles.npiBackButton}
        startIcon={<ArrowBackIosIcon fontSize="small" />}
      >
        Back
      </Button>
      <Box component="form" onSubmit={handleSubmit}>
        <div className={styles.npiInputContainer} data-testid="search-input-container" style={{ gap: 5 }}>
          <input
            className={styles.npiInput}
            style={{ width: '133px' }}
            onChange={(e) => setFirstName(e.target.value)}
            value={firstName}
            placeholder="First Name"
            data-testid="npi-first-name"
          ></input>
          <input
            className={styles.npiInput}
            style={{ width: '133px' }}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last Name"
            data-testid="npi-last-name"
            value={lastName}
          ></input>
        </div>
        <div className={styles.npiInputContainer} style={{ marginBottom: '25px' }}>
          <FormControl fullWidth className={styles.npiInput}>
            <Select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              displayEmpty
              renderValue={() => {
                return selectedStateName || 'State';
              }}
              sx={{
                '.MuiSelect-select': {
                  padding: 0,
                  paddingBlock: '12px',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'var(--npi-gray)',
                },
              }}
            >
              <MenuItem disabled value="">
                <em>State</em>
              </MenuItem>
              {states?.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            type="submit"
            data-testid="search-button"
            variant="contained"
            className={styles.npiSubmitButton}
            disabled={!(firstName && lastName && selectedState)}
          >
            Search
          </Button>
        </div>
      </Box>

      {loadingUserList ? (
        <div className={styles.loadingContainer}>
          <CircularProgress sx={{ color: 'var(--crystal)' }} />
        </div>
      ) : userList && userList.length > 0 ? (
        <div className={styles.npiResultContainer}>
          <Typography
            className={styles.npiResultText}
          >{`We found ${userList.length} candidates, please select below`}</Typography>
          {userList?.map((user: NpiUserListType, index: number) => {
            return (
              <div key={index}>
                <NPIUserDetails userDetails={user} onNpiSubmit={(npiNumber: string) => onNpiSelect(npiNumber)} />
              </div>
            );
          })}
        </div>
      ) : (
        userList?.length === 0 && (
          <div className={styles.noResultFound}>
            <Typography className={styles.npiResultText}>No Results Found</Typography>
            We couldn’t find any candidates matching your search criteria. Please double-check your information and try
            again. If the issue persists, feel free to contact support for assistance.
          </div>
        )
      )}
    </div>
  );
}

export function NPIUserDetails({ userDetails, onNpiSubmit }: NPIUserDetailsProps) {
  const { number, full_name, address, first_specialty } = userDetails;
  return (
    <div className={styles.user_details}>
      <div className={styles.left_side}>
        <Typography>{number}</Typography>
        <Button
          type="submit"
          variant="contained"
          className={styles.npiSelect_button}
          onClick={() => onNpiSubmit(number)}
        >
          Submit
        </Button>
      </div>
      <div className={styles.right_side}>
        <div>{full_name}</div>
        <div>{address}</div>
        <div>{first_specialty}</div>
      </div>
    </div>
  );
}
